import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  MenuItem,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import Pencil_img from "../../assets/baseVersion/blue-pen-icon.svg";
import IOSSwitch from "../../components/IOSSwitch";
import OrganisationSwitch from "./Organisation_Switch";

import JiraIntegration from "./JiraIntegration";
import axiosInstance from "../../axios/axios";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { END_POINTS } from "../../shared";
import ImageUploaderWithCrop from "./ImageUploaderWithCrop";
import { Modal } from "@mui/material";
import { useSelector } from "react-redux";
import { ROLES_CONTENT_PROPS } from "../../shared/constants/constant-data";
import useRolePermissions from "../../shared/hooks/useRolePermissions";
import {
  PasswordExpiryInfo,
  passwordExpiryError,
  passwordExpiryHint,
  passwordExpiryPayload,
} from "./passwordExpirySetting";

type JiraIntegrationData = {
  id: string;
  org_id: string;
  api_token: string;
  server: string;
  email_id: string;
  project: string;
  project_type_key: string;
  priority: string;
  type: string;
  assignee: string;
  is_submitted: boolean;
};

// authentication/org-details

const PencilIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 5,
  right: 5,
  backgroundColor: theme.palette.primary.main, // Set background to blue
  border: `2px solid ${theme.palette.background.paper}`,
  width: 28,
  height: 28,
  zIndex: 1,
  borderRadius: "50%", // Make the button circular
  display: "flex", // Center the pencil icon
  alignItems: "center", // Center vertically
  justifyContent: "center",
}));

const Organization = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [previousImage, setPreviousImage] = useState<string | null>("");
  const [image, setImage] = useState<string | null>("");
  const [isSavingSSO, setIsSavingSSO] = useState(false);
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJiraClicked, setIsJiraClicked] = useState(false);
  const [showSsoDropdown, setShowSsoDropdown] = useState(false);
  const [ssoProvider, setSsoProvider] = useState("");
  const [ssoList, setSsoList] = useState<{ id: string; name: string }[]>([]);
  const [isTurningOn, setIsTurningOn] = useState(true); // track if it's ON or OFF
  const [fetchedImage, setFetchedImage] = useState<string | null>(null);
  const [isJiraIntegrated, setIsJiraIntegrated] =
    useState<JiraIntegrationData | null>(null);
  interface OrganizationData extends PasswordExpiryInfo {
    parent_organization_name: string;
    organization_name: string;
    organization_address: string;
  }

  const organization_id = useSelector(
    (state: any) => state.UserLoginDetails.organization_id
  );

  const [organizationData, setOrganizationData] =
    useState<OrganizationData | null>(null);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { hasPermissions } = useRolePermissions();

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast("Only .jpg, .jpeg, and .png files are allowed", { type: "error" });
      return;
    }

    setPreviousImage(image);
    setSelectedFile(file);
    setIsUploadModalOpen(true);
    event.target.value = "";
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTurningOn(event.target.checked); // checked = true means turning ON
    setIsModalOpen(true);
  };

  const fetchOrganizationData = async () => {
    try {
      const response = await axiosInstance.get("/authentication/org-details");
      console.log(response.data?.data);
      setOrganizationData(response.data?.data);
    } catch (error) {
      console.error("Error fetching organization data:", error);
    }
  };

  // Call on component mount
  useEffect(() => {
    fetchOrganizationData();
    fetchSsoStatus();
    fetchSsoProviders();
  }, []);

  useEffect(() => {
    if (isSwitchOn) {
      setShowSsoDropdown(true);
    }
  }, [isSwitchOn]);
  useEffect(() => {
    const fetchUserImage = async () => {
      try {
        const response = await axiosInstance.get(
          "authentication/org-image?width=100&height=100",
          { responseType: "blob" }
        );

        const imageUrl = URL.createObjectURL(response.data);
        setFetchedImage(imageUrl);
      } catch (error) {
        console.error("Failed to fetch profile image:", error);
      }
    };

    fetchUserImage();
  }, []);

  const fetchSsoStatus = async () => {
    try {
      const response = await axiosInstance.get("/authentication/sso-org");
      const ssoEnabled = response.data?.data?.sso;
      setIsSwitchOn(ssoEnabled);
    } catch (error) {
      console.error("Failed to fetch SSO status:", error);
    }
  };

  const fetchSsoProviders = async () => {
    try {
      const response = await axiosInstance.get("/authentication/sso-master");
      const list = response.data?.data?.sso_list || [];
      setSsoList(list);
    } catch (error) {
      console.error("Failed to fetch SSO providers:", error);
    }
  };

  const fetchProjectList = async () => {
    try {
      const url = `jira-events/jira_integration?org_id=${organization_id}`;
      const response = await axiosInstance.get(url);

      // Jira IS integrated
      setIsJiraIntegrated(response?.data?.data || null);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        // Jira NOT integrated (expected case)
        setIsJiraIntegrated(null);
      } else {
        console.error("Error fetching Jira integration:", error);
        toast("Failed to fetch Jira integration", { type: "error" });
      }
    }
  };

  useEffect(() => {
    fetchProjectList();
  }, [organization_id]);

  const cancelUpload = () => {
    setImage(previousImage);
    setSelectedFile(null);
    setIsUploadModalOpen(false);
  };

  const formik = useFormik({
    // Reinitialize so the form starts from the saved details once they have loaded; the address is
    // required on every save, and the password expiry field needs its current value.
    enableReinitialize: true,
    initialValues: {
      address: organizationData?.organization_address,
      password_expiry_days: String(organizationData?.password_expiry_days ?? ""),
    },
    validate: (values) => {
      const message = passwordExpiryError(values.password_expiry_days, organizationData ?? {});
      return message ? { password_expiry_days: message } : {};
    },
    onSubmit: async (values) => {
      try {
        const req_payload = {
          organization_address: values.address,
          ...passwordExpiryPayload(values.password_expiry_days, organizationData ?? {}),
        };
        const response = await axiosInstance.post(
          END_POINTS.Eidt_Org,
          req_payload
        );

        toast(response.data.message || "Organization updated successfully", {
          type: "success",
          toastId: "org-update-success",
        });

        // Call refetch here
        await fetchOrganizationData();
        setIsEditClicked(false);
      } catch (error) {
        console.error("Failed to update organization address:", error);
        toast("Failed to update organization address", {
          type: "error",
          toastId: "org-update-error",
        });
      }
    },
  });

  const handleSaveSSO = async () => {
    if (isSavingSSO) return;
    if (!ssoProvider) {
      toast("Please select an SSO provider", {
        type: "warning",
        toastId: "sso-select-warning",
      });
      return;
    }

    try {
      setIsSavingSSO(true);
      const payload = {
        sso_id: ssoProvider,
        sso_status: true,
      };
      const response = await axiosInstance.post(
        "/authentication/sso-org",
        payload
      );
      toast(response.data.message || "SSO settings saved successfully", {
        type: "success",
        toastId: "sso-save-success",
      });

      // Optionally refresh org or sso status here
      await fetchSsoStatus();
      setShowSsoDropdown(false); // hide dropdown after saving
    } catch (error) {
      console.error("Failed to save SSO settings:", error);
      toast("Failed to save SSO settings", {
        type: "error",
        toastId: "sso-save-error",
      });
    } finally {
      setIsSavingSSO(false);
    }
  };

  const handleCancelSSO = () => {
    setIsSwitchOn(false);
    setShowSsoDropdown(false);
    setSsoProvider(""); // Optional: reset selected provider
  };

  return (
    <>
      <>
        {isJiraClicked ? (
          <div>
            <Drawer
              variant="temporary"
              anchor="right"
              open={isJiraClicked}
              onClose={() => setIsJiraClicked(false)}
              sx={{
                "& .MuiDrawer-paper": {
                  width: 400,
                  height: "100vh",
                  boxSizing: "border-box",
                  position: "fixed",
                  top: 0,
                  zIndex: 1300,
                  display: "flex",
                  flexDirection: "column",
                  overflowX: "hidden",
                  bgcolor: isDarkMode
                    ? "#1e1e1e"
                    : theme.palette.background.paper,
                  color: isDarkMode ? "#ffffff" : "#000000",
                },
                "& .MuiBackdrop-root": {
                  backdropFilter: "none",
                  backgroundColor: "transparent",
                },
              }}
            >
              <JiraIntegration
                setIsJiraClicked={setIsJiraClicked}
                isJiraIntegrated={isJiraIntegrated}
                fetchProjectListrefetch={fetchProjectList}
              />
            </Drawer>
          </div>
        ) : (
          <div>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  width: 120,
                  height: 120,
                  mb: 2,
                }}
              >
                <Avatar
                  src={image || fetchedImage || undefined}
                  sx={{ width: "100%", height: "100%" }}
                />
                {/* Pencil Icon for Upload */}
                <PencilIconButton as="label">
                  <EditIcon fontSize="small" sx={{ color: "white" }} />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageUpload}
                  />
                </PencilIconButton>
              </Box>
            </Box>

            {!isEditClicked ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "18px",
                      mb: 2,
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    {organizationData?.parent_organization_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "1000",
                      fontSize: "14px",
                      mb: 2,
                      color: isDarkMode ? "#3a2ada" : "blue",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setIsEditClicked(true);
                      setShowSsoDropdown(false);
                    }}
                  >
                    Edit
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#2D2D2D" : "#F7F7F9",
                    padding: "20px",
                    width: "1000w",
                    borderRadius: 3,
                    mb: 2,
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: "20px",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2">Organization Name</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: isDarkMode ? "#a0a0a0" : "#000000" }}
                    >
                      {organizationData?.organization_name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "20px",
                      justifyContent: "space-between",
                      mt: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">Address</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: isDarkMode ? "#a0a0a0" : "#000000" }}
                    >
                      {organizationData?.organization_address}
                    </Typography>
                  </Box>
                  {organizationData?.password_expiry_days != null && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "20px",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">Password expiry</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: isDarkMode ? "#a0a0a0" : "#000000" }}
                      >
                        {organizationData.password_expiry_days} days
                      </Typography>
                    </Box>
                  )}
                </Box>
                {hasPermissions(ROLES_CONTENT_PROPS.JIRA_VIEW_ONLY) && (
                  <Box
                    sx={{
                      backgroundColor: isDarkMode ? "#2D2D2D" : "#F7F7F9",
                      padding: "20px",
                      width: "1000w",
                      borderRadius: 3,
                      mb: 2,
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <>
                      {isJiraIntegrated ? (
                        <Box>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              Jira Integration
                            </Typography>
                            {hasPermissions(
                              ROLES_CONTENT_PROPS.JIRA_CREATE_MODIFY
                            ) && (
                                <img
                                  src={Pencil_img}
                                  alt=""
                                  style={{ width: "17px", cursor: "pointer" }}
                                  onClick={() => setIsJiraClicked(true)}
                                />
                              )}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">Org URL</Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.server}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              Project Short Code
                            </Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.project_type_key}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              User Email Id
                            </Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.email_id}
                            </Typography>
                          </div>
                        </Box>
                      ) : (
                        <Box>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2">
                              Jira Integration
                            </Typography>
                            <Typography variant="body2">
                              Not Integrated
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: 10,
                            }}
                          >
                            {hasPermissions(
                              ROLES_CONTENT_PROPS.JIRA_CREATE_MODIFY
                            ) && (
                                <img
                                  src={Pencil_img}
                                  alt=""
                                  style={{ width: "17px", cursor: "pointer" }}
                                  onClick={() => setIsJiraClicked(true)}
                                />
                              )}
                          </div>
                        </Box>
                      )}
                    </>
                  </Box>
                )}
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#2D2D2D" : "#F7F7F9",
                    padding: "20px",
                    width: "1000w",
                    borderRadius: 3,
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">SSO</Typography>
                    <IOSSwitch
                      checked={Boolean(isSwitchOn)}
                      onChange={handleSwitchChange}
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    backgroundColor: "#F7F7F9",
                    padding: "20px",
                    width: "1000w",
                    borderRadius: 3,
                    mb: 2,
                  }}
                >
                  <Box>
                    <>
                      {isJiraIntegrated ? (
                        <Box>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              Jira Integration
                            </Typography>
                            {hasPermissions(
                              ROLES_CONTENT_PROPS.JIRA_CREATE_MODIFY
                            ) && (
                                <img
                                  src={Pencil_img}
                                  alt=""
                                  style={{ width: "17px", cursor: "pointer" }}
                                  onClick={() => setIsJiraClicked(true)}
                                />
                              )}
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">Org URL</Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.server}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              Project Short Code
                            </Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.project_type_key}
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                              marginBottom: 10,
                            }}
                          >
                            <Typography variant="body2">
                              User Email Id
                            </Typography>
                            <Typography variant="body2">
                              {isJiraIntegrated.email_id}
                            </Typography>
                          </div>
                        </Box>
                      ) : (
                        <Box>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2">
                              Jira Integration
                            </Typography>
                            <Typography variant="body2">
                              Not Integrated
                            </Typography>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              marginTop: 10,
                            }}
                          >
                            {hasPermissions(
                              ROLES_CONTENT_PROPS.JIRA_CREATE_MODIFY
                            ) && (
                                <img
                                  src={Pencil_img}
                                  alt=""
                                  style={{ width: "17px", cursor: "pointer" }}
                                  onClick={() => setIsJiraClicked(true)}
                                />
                              )}
                          </div>
                        </Box>
                      )}
                    </>
                  </Box>
                </Box>
                <form onSubmit={formik.handleSubmit}>
                  <Box>
                    {" "}
                    <TextField
                      fullWidth
                      label="address"
                      id="address"
                      name="address"
                      value={formik.values.address}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.address && Boolean(formik.errors.address)
                      }
                      helperText={
                        formik.touched.address && formik.errors.address
                      }
                    />
                    {organizationData?.can_edit_password_expiry && (
                      <TextField
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Password expiry (days)"
                        id="password_expiry_days"
                        name="password_expiry_days"
                        type="number"
                        inputProps={{
                          min: organizationData.password_expiry_min_days,
                          max: organizationData.password_expiry_max_days,
                        }}
                        value={formik.values.password_expiry_days}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.password_expiry_days)}
                        helperText={
                          formik.errors.password_expiry_days ||
                          passwordExpiryHint(organizationData)
                        }
                      />
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 20,
                        marginTop: 25,
                      }}
                    >
                      <Button
                        variant="contained"
                        sx={{ width: 120 }}
                        type="submit"
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting ? "Submitting..." : "Submit"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditClicked(false)}
                        sx={{ width: 120 }}
                        type="button"
                        disabled={formik.isSubmitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Box>
                </form>
              </>
            )}
            {isModalOpen && (
              <div>
                <OrganisationSwitch
                  setIsSwitchOn={setIsSwitchOn}
                  isModalOpen={isModalOpen}
                  setIsModalOpen={setIsModalOpen}
                  setShowSsoDropdown={setShowSsoDropdown}
                  isTurningOn={isTurningOn}
                />
              </div>
            )}
            {isSwitchOn && (
              <>
                {showSsoDropdown && (
                  <Box mt={2}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Select SSO Provider
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      value={ssoProvider}
                      onChange={(e) => setSsoProvider(e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>

                      {ssoList.map((provider) => (
                        <MenuItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Save and Cancel buttons */}
                    <Box mt={2} display="flex" gap={2} justifyContent="center">
                      <Button
                        variant="contained"
                        onClick={handleSaveSSO}
                        disabled={isSavingSSO || !ssoProvider}
                      >
                        {isSavingSSO ? "Saving..." : "Save"}
                      </Button>
                      <Button variant="outlined" onClick={handleCancelSSO}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </div>
        )}
      </>
      <Modal open={isUploadModalOpen} onClose={cancelUpload}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          {selectedFile && (
            <ImageUploaderWithCrop
              file={selectedFile}
              onClose={cancelUpload}
              isOrg={true}
              onComplete={(previewUrl) => {
                setImage(previewUrl);
                setIsUploadModalOpen(false);
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Organization;
